/**
 * WB4.5 Execution Loop +4 — ACTION_SURFACE_PARITY_MATRIX SSOT.
 *
 * Maps canonical action ids → WorkbenchKernelExecutionSurface requirements.
 * Used by static gate + jest to ensure the same actionId is projected and
 * dispatchable on every required execution surface (7-path parity).
 */

import type { WorkbenchActionProjections, WorkbenchKernelExecutionSurface } from './action-types';
import {
  isDragDropHandlerRef,
  isInlineFieldHandlerRef,
  isInteractionNativeExecutionSurface,
  INTERACTION_SURFACE_DRAG_DROP_RESERVED_ACTION_IDS,
} from './workbench-interaction-surface-contract';
import { INTERACTION_SURFACE_INLINE_EDIT_PILOT_ACTION_IDS } from './workbench-inline-field-registry';
import {
  DOMAIN_BULK_ACTION_IDS,
  DOMAIN_BULK_STATE_CHANGE_ACTION_IDS,
  resolveDomainActionParityPilotDomains,
} from './domain-action-rule-table';
import {
  isManifestKernelHandlerRefDispatchable,
  resolveDomainHandlerDispatchLane,
} from './domain-kernel-handler-coverage';
import { SELLABLE_BULK_ACTION_IDS, SELLABLE_TOP_5_ACTION_IDS } from './sellable-action-rule-table';
import type { WorkbenchAction } from './action-types';
import type { ActionRuleEntry } from './action-types';

export const WORKBENCH_KERNEL_EXECUTION_SURFACES = [
  'button',
  'context_menu',
  'inspector',
  'palette',
  'bulk',
  'ai',
  'shortcut',
  'inline_edit',
  'drag_drop',
] as const satisfies readonly WorkbenchKernelExecutionSurface[];

const INLINE_EDIT_ONLY_SURFACES: readonly WorkbenchKernelExecutionSurface[] = ['inline_edit'];

const DRAG_DROP_ONLY_SURFACES: readonly WorkbenchKernelExecutionSurface[] = ['drag_drop'];

function buildInlineEditParityMatrixRows(): Record<
  string,
  readonly WorkbenchKernelExecutionSurface[]
> {
  const rows: Record<string, readonly WorkbenchKernelExecutionSurface[]> = {};
  for (const actionId of INTERACTION_SURFACE_INLINE_EDIT_PILOT_ACTION_IDS) {
    rows[actionId] = INLINE_EDIT_ONLY_SURFACES;
  }
  return rows;
}

/** Projection buckets checked before kernel dispatch on each execution surface. */
export const EXECUTION_SURFACE_PROJECTION_KEYS: Record<
  WorkbenchKernelExecutionSurface,
  readonly (keyof WorkbenchActionProjections)[]
> = {
  button: ['primary', 'secondary'],
  context_menu: ['contextMenu'],
  inspector: ['inspector'],
  palette: ['commandPalette'],
  bulk: ['bulk'],
  ai: ['aiSuggested'],
  shortcut: ['shortcuts'],
  inline_edit: [],
  drag_drop: [],
};

const ROW_DETAIL_SURFACES: readonly WorkbenchKernelExecutionSurface[] = [
  'button',
  'context_menu',
  'inspector',
  'palette',
];

const CONTEXT_INSPECTOR_PALETTE: readonly WorkbenchKernelExecutionSurface[] = [
  'context_menu',
  'inspector',
  'palette',
];

const BULK_ONLY_SURFACES: readonly WorkbenchKernelExecutionSurface[] = ['bulk'];

/**
 * Canonical action id → execution surfaces where projection + dispatch must succeed.
 * Bulk ids are bulk-only; AI pilot ids include `ai`; open_detail is shortcut-eligible.
 */
export const ACTION_SURFACE_PARITY_MATRIX: Readonly<
  Record<string, readonly WorkbenchKernelExecutionSurface[]>
> = {
  open_detail: [...ROW_DETAIL_SURFACES, 'shortcut'],
  edit: ROW_DETAIL_SURFACES,
  edit_description: [...CONTEXT_INSPECTOR_PALETTE, 'ai'],
  view_audit: CONTEXT_INSPECTOR_PALETTE,
  view_audit_bulk: BULK_ONLY_SURFACES,
  bulk_activate: BULK_ONLY_SURFACES,
  bulk_archive: BULK_ONLY_SURFACES,
  bulk_delete: BULK_ONLY_SURFACES,
  start_permission_workflow: CONTEXT_INSPECTOR_PALETTE,
  start_collection_contact_flow: [...CONTEXT_INSPECTOR_PALETTE, 'ai'],
  record_collection_commitment: [...CONTEXT_INSPECTOR_PALETTE, 'ai'],
  start_supplier_goods_receipt_flow: [...CONTEXT_INSPECTOR_PALETTE, 'ai'],
  start_accounting_workflow: [...CONTEXT_INSPECTOR_PALETTE, 'ai'],
  start_inventory_workflow: [...CONTEXT_INSPECTOR_PALETTE, 'ai'],
  start_production_workflow: [...CONTEXT_INSPECTOR_PALETTE, 'ai'],
  start_order_workflow: [...CONTEXT_INSPECTOR_PALETTE, 'ai'],
  start_order: CONTEXT_INSPECTOR_PALETTE,
  start_assignment_workflow: [...CONTEXT_INSPECTOR_PALETTE, 'ai'],
  'view-related-records': [...CONTEXT_INSPECTOR_PALETTE, 'ai'],
  'copy-object-id': CONTEXT_INSPECTOR_PALETTE,
  'reference:attach_evidence': CONTEXT_INSPECTOR_PALETTE,
  ...buildInlineEditParityMatrixRows(),
  'drag_drop:assign_task': DRAG_DROP_ONLY_SURFACES,
  'drag_drop:attach_evidence': DRAG_DROP_ONLY_SURFACES,
  'drag_drop:start_production_flow': DRAG_DROP_ONLY_SURFACES,
  'drag_drop:route_to_picking_queue': DRAG_DROP_ONLY_SURFACES,
  'drag_drop:route_to_collection_queue': DRAG_DROP_ONLY_SURFACES,
  'drag_drop:attach_approval_packet': DRAG_DROP_ONLY_SURFACES,
  'drag_drop:create_purchase_request': DRAG_DROP_ONLY_SURFACES,
  'drag_drop:link_document_to_order': DRAG_DROP_ONLY_SURFACES,
  'drag_drop:link_order_to_customer': DRAG_DROP_ONLY_SURFACES,
} as const;

/** WB4-IX0 — interaction pilot + reserved ids for matrix coverage gates. */
export const INTERACTION_SURFACE_PARITY_ACTION_IDS = [
  ...INTERACTION_SURFACE_INLINE_EDIT_PILOT_ACTION_IDS,
  ...INTERACTION_SURFACE_DRAG_DROP_RESERVED_ACTION_IDS,
] as const;

/** Legacy core-table audit ref — domain manifests must use wired viewAudit handler instead. */
export const KERNEL_REGISTRY_BLOCKED_HANDLER_REFS = new Set<string>(['navigate:audit']);

/**
 * Auto-derived from domain-action-rule-table family anchors (+ sellable + depth samples).
 * Do not edit manually — extend DOMAIN_ACTION_PARITY_FAMILY_ANCHORS instead.
 */
export const ACTION_SURFACE_PARITY_DOMAIN_SAMPLES = resolveDomainActionParityPilotDomains();

export const FINANCE_DETAIL_PARITY_DOMAINS = [
  'finance_state',
  'finance_flow',
  'finance_budget',
  'stream_execution',
] as const;

/** Manufacturing detail-header kernel dispatch — deferred from WB4.5 finance pilot rollout. */
export const MANUFACTURING_DETAIL_PARITY_DOMAINS = ['quality_inspection'] as const;

export const CANONICAL_PARITY_ACTION_IDS = [
  ...new Set([
    ...Object.keys(ACTION_SURFACE_PARITY_MATRIX),
    ...SELLABLE_TOP_5_ACTION_IDS,
    ...SELLABLE_BULK_ACTION_IDS,
    ...DOMAIN_BULK_ACTION_IDS,
    ...DOMAIN_BULK_STATE_CHANGE_ACTION_IDS,
  ]),
] as const;

export function resolveActionSurfaceParityRequirements(
  actionId: string
): readonly WorkbenchKernelExecutionSurface[] | undefined {
  return ACTION_SURFACE_PARITY_MATRIX[actionId];
}

export function isActionProjectedOnExecutionSurface(
  projections: WorkbenchActionProjections,
  actionId: string,
  surface: WorkbenchKernelExecutionSurface
): boolean {
  const keys = EXECUTION_SURFACE_PROJECTION_KEYS[surface];
  return keys.some((key) => projections[key].some((action) => action.id === actionId));
}

export interface ActionSurfaceParityViolation {
  actionId: string;
  surface?: WorkbenchKernelExecutionSurface;
  reason: string;
}

export function collectActionSurfaceParityViolations(input: {
  actionId: string;
  handlerRef: string;
  projections: WorkbenchActionProjections;
}): ActionSurfaceParityViolation[] {
  const required = resolveActionSurfaceParityRequirements(input.actionId);
  if (!required) {
    return [];
  }

  const violations: ActionSurfaceParityViolation[] = [];

  if (KERNEL_REGISTRY_BLOCKED_HANDLER_REFS.has(input.handlerRef)) {
    violations.push({
      actionId: input.actionId,
      reason: `blocked noop handlerRef ${input.handlerRef}`,
    });
  }

  if (!isManifestKernelHandlerRefDispatchable(input.handlerRef)) {
    violations.push({
      actionId: input.actionId,
      reason: `unwired handlerRef ${input.handlerRef} (${resolveDomainHandlerDispatchLane(input.handlerRef)})`,
    });
  }

  for (const surface of required) {
    if (isInteractionNativeExecutionSurface(surface)) {
      if (!isInteractionSurfaceHandlerDispatchable(input.handlerRef, surface)) {
        violations.push({
          actionId: input.actionId,
          surface,
          reason: `unwired interaction handlerRef ${input.handlerRef}`,
        });
      }
      continue;
    }
    if (!isActionProjectedOnExecutionSurface(input.projections, input.actionId, surface)) {
      violations.push({
        actionId: input.actionId,
        surface,
        reason: `missing ${surface} projection`,
      });
    }
  }

  return violations;
}

export function collectRuleTableHandlerViolations(
  table: readonly ActionRuleEntry[],
  label: string
): string[] {
  const violations: string[] = [];

  for (const entry of table) {
    for (const action of entry.actions) {
      if (KERNEL_REGISTRY_BLOCKED_HANDLER_REFS.has(action.handlerRef)) {
        violations.push(
          `${label}: action ${action.id} uses blocked handlerRef ${action.handlerRef}`
        );
      }
      if (!isManifestKernelHandlerRefDispatchable(action.handlerRef)) {
        violations.push(
          `${label}: action ${action.id} → ${action.handlerRef} is unwired (${resolveDomainHandlerDispatchLane(action.handlerRef)})`
        );
      }
    }
  }

  return violations;
}

export function isInteractionSurfaceHandlerDispatchable(
  handlerRef: string,
  surface: WorkbenchKernelExecutionSurface
): boolean {
  if (surface === 'inline_edit') {
    return isInlineFieldHandlerRef(handlerRef);
  }
  if (surface === 'drag_drop') {
    return isDragDropHandlerRef(handlerRef);
  }
  return false;
}

export function collectInteractionSurfaceParityViolations(): string[] {
  const violations: string[] = [];
  for (const actionId of INTERACTION_SURFACE_PARITY_ACTION_IDS) {
    if (!ACTION_SURFACE_PARITY_MATRIX[actionId]) {
      violations.push(
        `interaction: action id ${actionId} missing from ACTION_SURFACE_PARITY_MATRIX`
      );
    }
  }
  return violations;
}

export function collectMatrixCoverageViolations(
  actionIds: readonly string[],
  scopeLabel: string
): string[] {
  const violations: string[] = [];

  for (const actionId of actionIds) {
    if (!ACTION_SURFACE_PARITY_MATRIX[actionId]) {
      violations.push(
        `${scopeLabel}: action id ${actionId} missing from ACTION_SURFACE_PARITY_MATRIX`
      );
    }
  }

  return violations;
}

export function collectAiPilotRegistryViolations(
  pilotIdsByDomain: Readonly<Record<string, readonly string[]>>
): string[] {
  const violations: string[] = [];

  for (const [domain, actionIds] of Object.entries(pilotIdsByDomain)) {
    for (const actionId of actionIds) {
      const required = resolveActionSurfaceParityRequirements(actionId);
      if (!required) {
        violations.push(
          `ai pilot ${domain}: ${actionId} missing from ACTION_SURFACE_PARITY_MATRIX`
        );
        continue;
      }
      if (!required.includes('ai')) {
        violations.push(`ai pilot ${domain}: ${actionId} must require ai execution surface`);
      }
    }
  }

  return violations;
}

export function formatActionSurfaceParityViolation(
  scope: string,
  violation: ActionSurfaceParityViolation
): string {
  const surface = violation.surface ? ` · ${violation.surface}` : '';
  return `${scope}: ${violation.actionId}${surface} — ${violation.reason}`;
}

/** Static rule-table gate — blocks noop handler refs at manifest SSOT (not dynamic registerWorkbenchActions). */
export function assertStaticRuleTableHandlersWired(
  table: readonly ActionRuleEntry[],
  label: string
): void {
  const violations = collectRuleTableHandlerViolations(table, label);
  if (violations.length > 0) {
    throw new Error(violations.join('\n'));
  }
}

export type ParityScopedAction = Pick<WorkbenchAction, 'id' | 'handlerRef'>;
